import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

interface BreadcrumbLabelContextValue {
  label: string | null;
  setLabel: (label: string | null) => void;
}

const BreadcrumbLabelContext = createContext<BreadcrumbLabelContextValue | null>(null);

export const BreadcrumbLabelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [label, setLabel] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    setLabel(null);
  }, [location.pathname]);

  const value = useMemo(() => ({ label, setLabel }), [label]);

  return (
    <BreadcrumbLabelContext.Provider value={value}>{children}</BreadcrumbLabelContext.Provider>
  );
};

export function useBreadcrumbLabelContext(): BreadcrumbLabelContextValue {
  const ctx = useContext(BreadcrumbLabelContext);
  if (!ctx) {
    throw new Error('useBreadcrumbLabelContext must be used within BreadcrumbLabelProvider');
  }
  return ctx;
}

/** Set the display label for the current route's dynamic breadcrumb segment (e.g. entity name). */
export function useBreadcrumbLabel(label: string | undefined | null) {
  const { setLabel } = useBreadcrumbLabelContext();

  useEffect(() => {
    if (label?.trim()) {
      setLabel(label.trim());
    }
    return () => setLabel(null);
  }, [label, setLabel]);
}
